/**
 * Channel/shell access — port of galaxy-prototype `ROLE_CHANNELS`.
 *
 * **Universal isolation (all shells):** hard URL / deep-link must not open a
 * channel the persona is not assigned to. Enforced by `RequireShellAccess`
 * for Ops · Field · EXE. Print is not a persona channel.
 *
 * EXE alerts stay under `/exe/ref/*` — no Ops escape.
 */

export type AccessShell = "ops" | "field" | "exe";

/** Role → allowed shells (ops=WEB · field=MOB · exe=EXE). */
export const ROLE_SHELLS: Record<string, readonly AccessShell[]> = {
  ROLE_KE_HOACH: ["ops"],
  ROLE_KINH_DOANH: ["ops"],
  ROLE_MUA_HANG: ["ops"],
  ROLE_QD: ["ops", "field"],
  ROLE_NV_SX: ["field", "ops"],
  ROLE_QC: ["ops", "field"],
  ROLE_QA: ["ops"],
  ROLE_KHO: ["ops", "field"],
  ROLE_ADMIN: ["ops"],
  ROLE_BGD: ["exe"],
};

function roleCodes(
  roles: readonly string[] | readonly { code: string }[],
): string[] {
  return roles.map((r) => (typeof r === "string" ? r : r.code).toUpperCase());
}

/** Union of shells granted by any role. Unknown role → ops only. */
export function allowedShellsForRoles(
  roles: readonly string[] | readonly { code: string }[],
): AccessShell[] {
  const codes = roleCodes(roles);
  if (codes.length === 0) return ["ops"];
  const set = new Set<AccessShell>();
  for (const code of codes) {
    const shells = ROLE_SHELLS[code];
    if (shells) {
      for (const s of shells) set.add(s);
    } else {
      set.add("ops");
    }
  }
  return [...set];
}

export function canAccessShell(
  roles: readonly string[] | readonly { code: string }[],
  shell: AccessShell,
): boolean {
  return allowedShellsForRoles(roles).includes(shell);
}

export function shellForPath(pathname: string): AccessShell | null {
  const clean = (pathname.split(/[?#]/)[0]?.trim() ?? "").toLowerCase();
  if (clean === "/ops" || clean.startsWith("/ops/")) return "ops";
  if (clean === "/field" || clean.startsWith("/field/")) return "field";
  if (clean === "/exe" || clean.startsWith("/exe/")) return "exe";
  return null;
}

/** Shell access by path — no cross-shell exceptions. */
export function canAccessPath(
  roles: readonly string[] | readonly { code: string }[],
  pathname: string,
): boolean {
  const shell = shellForPath(pathname);
  if (!shell) return true;
  return canAccessShell(roles, shell);
}
